import moment from "moment"

/**
 * Format hours for display: 1 → "1h", 1.5 → "1h30", 2 → "2h"
 */
export const formatHoursPerWeek = (hours) => {
    if (hours == null || isNaN(hours)) return "0h"
    const wholeHours = Math.floor(hours)
    const minutes = Math.round((hours - wholeHours) * 60)
    if (minutes === 0) return `${wholeHours}h`
    if (wholeHours === 0) return `${minutes}min`
    return `${wholeHours}h${minutes}`
}

// Synthetic weekday spread used when the student has no real recurring slots.
// Mirrors the backend SessionScheduleSimulator fallback (same day priority).
const buildSyntheticSchedule = (sessionsPerWeek) => {
    const defaultDays = ["Monday", "Thursday", "Tuesday", "Friday", "Wednesday"]
    const schedule = []
    for (let i = 0; i < sessionsPerWeek && i < defaultDays.length; i++) {
        schedule.push(`${defaultDays[i]} 18:00`)
    }
    return schedule
}

/**
 * Mirror of the backend SessionScheduleSimulator::simulateSessionsCreditInDateRange.
 * Simulates the sessions in a window of the pause-start month and returns the
 * prorated euro amount, capped at the full monthly price.
 *
 * With the student's real slots the amount is prorated on the month's ACTUAL
 * session count (skipping every session refunds the full month); otherwise a
 * synthetic weekday spread keeps the legacy nominal per-session value.
 */
const simulateSessionsCreditInDateRange = (
    rangeStart,
    rangeEnd,
    hoursPerMonth,
    hourlyRate,
    weeklySchedule,
    sessionsPerWeekMap
) => {
    const start = (
        moment.isMoment(rangeStart) ? rangeStart : moment(rangeStart)
    )
        .clone()
        .startOf("day")
    const startOfMonth = start.clone().startOf("month")
    const endOfMonth = start.clone().endOf("month")
    let end = (moment.isMoment(rangeEnd) ? rangeEnd : moment(rangeEnd)).clone()
    if (end.isAfter(endOfMonth)) end = endOfMonth.clone()

    const hoursPerWeek =
        sessionsPerWeekMap?.[hoursPerMonth] ?? Math.round(hoursPerMonth / 4.35)
    const sessionsPerWeek = Math.ceil(hoursPerWeek)
    // The real schedule's weekly slot count must stay within 2 of the nominal
    // weekly count; a larger gap signals an untrusted schedule (stale/dup rows,
    // multi-room, corruption) and we fall back to the synthetic nominal calc.
    const realSessionsPerWeek = Array.isArray(weeklySchedule)
        ? weeklySchedule.length
        : 0
    const hasRealSchedule =
        realSessionsPerWeek > 0 &&
        Math.abs(realSessionsPerWeek - sessionsPerWeek) <= 2
    const schedule = hasRealSchedule
        ? weeklySchedule
        : buildSyntheticSchedule(sessionsPerWeek)

    const monthlyPrice = Math.round(hourlyRate * hoursPerMonth * 100) / 100
    // Sessions billed across the whole month — the denominator for proportional
    // crediting (a fully-skipped month must refund in full).
    const totalSessions = simulateSessionsInDateRange(
        startOfMonth,
        endOfMonth,
        schedule
    )

    if (start.isSameOrAfter(end, "day")) {
        return 0
    }

    const missedSessions = simulateSessionsInDateRange(start, end, schedule)
    const credit =
        hasRealSchedule && totalSessions > 0
            ? Math.round(
                  ((monthlyPrice * missedSessions) / totalSessions) * 100
              ) / 100
            : Math.round(missedSessions * hourlyRate * 100) / 100

    return Math.min(monthlyPrice, credit)
}

/**
 * Calculate credits for the start month of a pause (from pause start to end of month)
 * @param {Date|string|moment} pauseStartDate - When the pause starts
 * @param {Number} hoursPerMonth - Hours per month in the subscription
 * @param {Number} hourlyRate - Rate per hour
 * @param {Array} weeklySchedule - Weekly schedule pattern e.g. ["Monday 18:00", "Tuesday 18:00"]
 * @returns {Number} Credit amount for start month in euros
 */
export const calculateStartMonthCredit = (
    pauseStartDate,
    hoursPerMonth = 9,
    hourlyRate = 20,
    weeklySchedule = [],
    sessionsPerWeekMap = null
) => {
    const pauseStart = moment.isMoment(pauseStartDate)
        ? pauseStartDate
        : moment(pauseStartDate)
    const endOfMonth = moment(pauseStart).endOf("month")

    return simulateSessionsCreditInDateRange(
        pauseStart,
        endOfMonth,
        hoursPerMonth,
        hourlyRate,
        weeklySchedule,
        sessionsPerWeekMap
    )
}

/**
 * Calculate the prorated amount charged for the resume month of a pause
 * (value of the sessions occurring on/after the resume date that month).
 * @param {Date|string|moment} resumeDate - When the subscription resumes
 * @param {Number} hoursPerMonth - Hours per month in the subscription
 * @param {Number} hourlyRate - Rate per hour
 * @param {Array} weeklySchedule - Weekly schedule pattern e.g. ["Monday 18:00", "Tuesday 18:00"]
 * @returns {Number} Prorated charge for the resume month in euros
 */
export const calculateResumeMonthProratedAmount = (
    resumeDate,
    hoursPerMonth = 9,
    hourlyRate = 20,
    weeklySchedule = [],
    sessionsPerWeekMap = null
) => {
    const resume = moment.isMoment(resumeDate) ? resumeDate : moment(resumeDate)
    const endOfMonth = moment(resume).endOf("month")

    return simulateSessionsCreditInDateRange(
        resume,
        endOfMonth,
        hoursPerMonth,
        hourlyRate,
        weeklySchedule,
        sessionsPerWeekMap
    )
}

/**
 * Simulate the number of live sessions that would occur in a given date range
 * @param {Date|string|moment} startDate - Start date of the range
 * @param {Date|string|moment} endDate - End date of the range
 * @param {Array} weeklySchedule - Weekly schedule pattern (e.g., ['Monday 15:00', 'Wednesday 16:00'])
 * @returns {Number} Number of sessions
 */
export const simulateSessionsInDateRange = (
    startDate,
    endDate,
    weeklySchedule
) => {
    if (
        !weeklySchedule ||
        !Array.isArray(weeklySchedule) ||
        weeklySchedule.length === 0
    ) {
        return 0
    }

    // Convert dates to moment objects if they aren't already
    const start = moment.isMoment(startDate) ? startDate : moment(startDate)
    const end = moment.isMoment(endDate) ? endDate : moment(endDate)

    // Parse the weekly schedule into day and time
    const scheduledDays = weeklySchedule.map((schedule) => {
        // Format is "Monday 18:00"
        const [day, time] = schedule.split(" ")
        return { day, time }
    })

    let sessionsCount = 0
    const dayMap = {
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
        Sunday: 0,
    }

    // Clone the start date to avoid modifying the original
    const currentDate = moment(start)

    // Iterate through each day in the range
    while (currentDate.isSameOrBefore(end, "day")) {
        // moment days are 0-6, 0 = Sunday
        const dayOfWeek = currentDate.day()

        // Check if this day matches any in the schedule
        scheduledDays
            .filter((schedule) => dayMap[schedule.day] === dayOfWeek)
            ?.forEach(() => {
                sessionsCount++
            })

        // Move to the next day
        currentDate.add(1, "day")
    }

    return sessionsCount
}
